import * as yup from 'yup';
import { useState } from 'react';
import { Container } from "react-bootstrap";
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Alert, CircularProgress } from "@mui/material";
import { yupResolver } from '@hookform/resolvers/yup';
import AddressTable from "../forms/components/AddressTable";
import { createAddress, getAddress, removeAddress, updateDefaultAddress } from '../api/Address.api';

const addressSchema = yup.object().shape({
  company: yup.string().required('Name is required'),
  street1: yup.string().required('Number & Street is required'),
  suburb: yup.string().required('Suburb is required'),
  state: yup.string().required('State is required'),
  postcode: yup.string().required('Postcode is required'),
});

const checkStyle = {
  grey: {
    cursor: 'pointer',
    color: 'grey',
    zIndex: 10
  },
  green: {
    cursor: 'pointer',
    color: 'green',
    zIndex: 10
  }
}

function AddressBook() {
  /** status can be error, success, warning using Alert component */
  const [statusMessage, setStatusMessage] = useState({ message: '', status: 'success' });
  const [isAdding, setIsAdding] = useState(false);

  const { isLoading, error, data: addresses, refetch: refetchAddresses } = useQuery({
    queryKey: ['address-book'],
    queryFn: getAddress,
    staleTime: 1000 * 60 * 30, // 30 minutes
    defaultValues: []
  });

  const methods = useForm({
    resolver: yupResolver(addressSchema),
    defaultValues: {
      company: '',
      street1: '',
      suburb: '',
      state: '',
      postcode: ''
    }
  })
  const { reset, clearErrors, handleSubmit } = methods;

  const addTableRow = (e) => {
    e.preventDefault();
    setIsAdding(true);
    setStatusMessage({});
  }

  const deleteTableRow = () => {
    clearErrors();
    setIsAdding(false);
  }

  const onSubmit = async (data) => {
    createAddress(data)
      .then(response => {
        if (response.status === 200) {
          setStatusMessage({message: 'Your address was saved.', status: 'success'});
          handleReset();
        }
      }).catch((error) => {
        setStatusMessage({message: 'Your address was not saved.', status: 'error'});
        console.log(error);
      }).finally(() => {
        deleteTableRow();
        refetchAddresses();
      });
  }

  /**
   * @param string id
   * @param billing | shipping method
   */
  const handleAddressDefault = async (id, method) => {
    updateDefaultAddress({ id: id, method: method })
      .then(() => {
          setStatusMessage({message: 'Default ' + method + ' address was flagged.', status: 'success'});
      }).finally(() => refetchAddresses());
  }

  const handleAddressRemove = async (id) => {
    removeAddress({ id: id })
      .then((response) => {
          if (response.status === 200) {
            setStatusMessage({message: 'Your address has been deleted', status: 'success'});
          }
      }).finally(() => refetchAddresses());
  }

  const handleReset = () => {
    reset();
  }

  if (error) console.log(error); 

  return (
    <Container>
      <div className="mb-5">
        <h2>Address Book</h2>
        <p>To make the order process quicker, iStore Shop provides a way to store your most common delivery addresses for a more convenient checkout process.</p>
        {statusMessage.message && <Alert className="border mb-3" severity={statusMessage.status}>{statusMessage.message}</Alert>}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <table className="" id="my-orders-table">
              <thead>
                <tr className="text-black">
                  <th className="id" colSpan="5"></th>
                  <th className="address-tab text-center bg-secondary bg-opacity-50" colSpan="2">Default Addresses</th>
                  <th className="blank"></th>
                </tr>
                <tr className="text-black">
                  <th className="date">Name</th>
                  <th className="date">Number & Street</th>
                  <th style={{width: '20%'}} className="total">Suburb</th>
                  <th style={{width: '10%'}} className="status text-center">State</th>
                  <th style={{width: '10%'}} className="status text-center">Postcode</th>
                  <th style={{width: '85px'}} className="actions text-center border">Shipping</th>
                  <th style={{width: '85px'}} className="actions text-center border">Billing</th>
                  <th style={{width: '30px'}} className="actions"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8}>
                      <div className='d-flex justify-content-center'>
                        <CircularProgress/>
                      </div>
                    </td>
                  </tr>
                ) : (
                  addresses?.map((address)  => {
                    return (
                      <tr key={address.id}>
                        <td className="border">{address.company}</td>
                        <td className="border">{address.street1}</td>
                        <td className="border">{address.suburb}</td>
                        <td className="border">{address.state}</td>
                        <td className="border">{address.postcode}</td>
                        <td style={{ width: '85px' }} className="border text-center">
                          <button 
                            className='bg-transparent border-0'
                            title="Set this address as default Shipping address"
                            onClick={(e) => {
                              e.preventDefault();
                              handleAddressDefault(address.id, 'shipping')
                            }}
                          >
                            <i className="fas fa-check-circle" style={address.shipping ? checkStyle.green : checkStyle.grey} />
                          </button>
                        </td>

                        <td style={{ width: '85px' }} className="border text-center">
                           <button 
                            className='bg-transparent border-0'
                            title="Set this address as default Billing address"
                            onClick={(e) => {
                              e.preventDefault();
                              handleAddressDefault(address.id, 'billing')
                            }}
                          >
                            <i className="fas fa-check-circle" style={address.billing ? checkStyle.green : checkStyle.grey} />
                          </button>
                        </td>

                        <td style={{width: '60px'}} data-th="Actions" className="border">
                          <button 
                            className='bg-transparent border-0'
                            title="Set this address as default Billing address"
                            onClick={(e) => {
                              e.preventDefault();
                              handleAddressRemove(address.id)
                            }}
                          >
                            <i className="fas fa-trash"/>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
                <AddressTable isAdding={isAdding} deleteTableRow={deleteTableRow} addTableRow={addTableRow} />
              </tbody>
            </table>
          </form>
        </FormProvider>
      </div>
    </Container>
  );
}

export default AddressBook;